import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Eye,
  ThumbsUp,
  ThumbsDown } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusConfig = {
  draft: { label: 'Draft', variant: 'outline', icon: FileText },
  pending_approval: { label: 'Pending', variant: 'secondary', icon: Clock },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle2 },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle },
  converted: { label: 'Converted', variant: 'default', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', variant: 'destructive', icon: XCircle }
};

export const PurchaseRequisitions = () => {const { t } = useTranslation("procurement");
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchRequisitions = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/procurement/requisitions`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch requisitions');
      const data = await response.json();
      setRequisitions(data);
    } catch (err) {
      toast.error('Failed to load requisitions');
      setRequisitions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequisitions();
  }, []);

  const handleApprove = async (prId) => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/requisitions/${prId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to approve');
      toast.success('Requisition approved');
      fetchRequisitions();
    } catch (err) {
      toast.error('Failed to approve requisition');
    }
  };

  const handleReject = async (prId) => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/requisitions/${prId}/reject`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to reject');
      toast.success('Requisition rejected');
      fetchRequisitions();
    } catch (err) {
      toast.error('Failed to reject requisition');
    }
  };

  const filteredRequisitions = requisitions.filter((pr) => {
    const matchesSearch = (pr.pr_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (pr.vendor_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (pr.justification || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || pr.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: requisitions.length,
    draft: requisitions.filter((r) => r.status === 'draft').length,
    pending: requisitions.filter((r) => r.status === 'pending_approval').length,
    approved: requisitions.filter((r) => r.status === 'approved').length,
    rejected: requisitions.filter((r) => r.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="purchase-requisitions">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("purchaseRequisitions", "Purchase Requisitions")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("createAndManage", "Create and manage purchase requests")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchRequisitions} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4" />{t("newRequisition", "New Requisition")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">{t("total", "Total")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-400">{stats.draft}</p>
              <p className="text-xs text-muted-foreground">{t("draft", "Draft")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-500">{stats.pending}</p>
              <p className="text-xs text-muted-foreground">{t("pending", "Pending")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-500">{stats.approved}</p>
              <p className="text-xs text-muted-foreground">{t("approved", "Approved")}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
              <p className="text-xs text-muted-foreground">{t("rejected", "Rejected")}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search requisitions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9" />
          
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'draft', 'pending_approval', 'approved', 'rejected'].map((status) =>
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            onClick={() => setStatusFilter(status)}
            size="sm"
            className="capitalize">
            
              {status === 'pending_approval' ? 'Pending' : status}
            </Button>
          )}
        </div>
      </div>

      {/* Requisitions Table */}
      <Card>
        <CardContent className="p-0">
          {filteredRequisitions.length === 0 ?
          <div className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 text-slate-600 mb-4" />
              <p className="text-slate-400 text-lg">{t("noRequisitionsFound", "No requisitions found")}</p>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("prNumber", "PR Number")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("justification", "Justification")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("date", "Date")}</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">{t("amount", "Amount")}</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">{t("status", "Status")}</th>
                    <th className="text-right p-4 font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequisitions.map((pr) => {
                  const config = statusConfig[pr.status] || statusConfig.draft;
                  const StatusIcon = config.icon;
                  return (
                    <tr key={pr.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="p-4">
                          <span className="font-mono font-medium">{pr.pr_number}</span>
                        </td>
                        <td className="p-4">{pr.vendor_name || 'TBD'}</td>
                        <td className="p-4 max-w-xs truncate text-muted-foreground">
                          {pr.justification || 'No justification'}
                        </td>
                        <td className="p-4 text-muted-foreground">
                          {pr.created_at ? new Date(pr.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-4 text-right font-medium">
                          ${(pr.total_amount || 0).toLocaleString()}
                        </td>
                        <td className="p-4">
                          <Badge variant={config.variant} className="gap-1">
                            <StatusIcon className="h-3 w-3" />
                            {config.label}
                          </Badge>
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex gap-2 justify-end">
                            {pr.status === 'pending_approval' &&
                          <>
                                <Button
                              size="sm"
                              variant="outline"
                              className="text-green-500 border-green-500 hover:bg-green-500/10"
                              onClick={() => handleApprove(pr.id)}>
                              
                                  <ThumbsUp className="h-4 w-4" />
                                </Button>
                                <Button
                              size="sm"
                              variant="outline"
                              className="text-red-500 border-red-500 hover:bg-red-500/10"
                              onClick={() => handleReject(pr.id)}>
                              
                                  <ThumbsDown className="h-4 w-4" />
                                </Button>
                              </>
                          }
                            <Button size="sm" variant="ghost">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>);

                })}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};

export default PurchaseRequisitions;